define([
    'santa-components',
    'lodash',
    'prop-types',
    'componentsCore',
    'santa-core-utils',
    'mediaControls/mediaControlStoryboard/mediaControlStoryboard'
], function (
    santaComponents,
    _,
    PropTypes,
    componentsCore,
    coreUtilsLib,
    mediaControlStoryboard
) {
    'use strict';

    const consts = coreUtilsLib.mediaConsts;
    const SimpleDrag = coreUtilsLib.SimpleDrag;

    function isSeeking(playbackState) {
        return _.includes([
            consts.playbackTypes.SEEK_PLAYING,
            consts.playbackTypes.SEEK_PAUSED,
            consts.playbackTypes.SEEKING
        ], playbackState);
    }

    /**
     * @class components.MediaControlProgress
     * @extends {core.skinBasedComp}
     */
    return {
        displayName: 'MediaControlProgress',
        mixins: [componentsCore.mixins.skinBasedComp, componentsCore.mixins.createChildComponentMixin],

        propTypes: _.defaults(
            {
                style: PropTypes.object,
                playerId: PropTypes.string.isRequired,
                mediaAspect: santaComponents.santaTypesDefinitions.SiteAspects.mediaAspect.isRequired,
                handleBehavior: santaComponents.santaTypesDefinitions.Behaviors.handleBehavior.isRequired,
                serviceTopologyStaticVideoUrl: santaComponents.santaTypesDefinitions.ServiceTopology.staticVideoUrl,
                showStoryboard: PropTypes.string,
                storyboardUrl: PropTypes.string,
                videoAspectRatio: PropTypes.number,
                svgPropsMapForMediaControls: PropTypes.object
            },
            santaComponents.utils.santaTypesUtils.getSantaTypesByDefinition(mediaControlStoryboard)
        ),

        getInitialState() {
            this.isDragging = false;
            this.setStoryboardState = null;
            this.lastPlayheadPosition = 0;
            return {
                playbackState: consts.playbackTypes.WAITING,
                currentTime: 0,
                progress: 0,
                duration: 0,
                isStoryboardVisible: false,
                storyboardPlaybackError: false
            };
        },

        getDefaultProps() {
            return {
                showVideoInStoryboard: true,
                showProgressBuffer: false
            };
        },

        /**
         * Register to media aspect
         */
        componentDidMount() {
            this.props.mediaAspect.registerStateChange(this.props.id, this.props.playerId, this.setState.bind(this));
        },

        /**
         * Unregister form media aspect
         */
        componentWillUnmount() {
            this.props.mediaAspect.unregisterStateChange(this.props.id, this.props.playerId);
        },

        componentWillUpdate(nextProps, nextState) {
            //clear the storyboard error if file past availability
            if (nextState.storyboardReadyState === 'idle' && this.state.storyboardPlaybackError) {
                this.setState({storyboardPlaybackError: false});
            }
        },

        /**
         * When starting to drag the playhead, update position and notify the player we are starting to seek
         * This will update the seek state but will not notify the video untill drag has ended
         * @param event
         * @param state
         */
        onSeekStart(event, state) {
            this.isDragging = true;

            this.lastPlayheadPosition = `${state.endX / state.maxX * 100}%`;
            this.refs.playhead.style.left = this.lastPlayheadPosition;

            this.props.handleBehavior({
                type: 'comp',
                targetId: this.props.playerId,
                name: 'seekStart'
            });
        },

        /**
         * When dragging the playhead finished, update the video with the new time
         * @param event
         * @param state
         */
        onSeekEnd(event, state) {
            const time = state.endX / state.maxX * this.state.duration;

            this.props.handleBehavior({
                type: 'comp',
                targetId: this.props.playerId,
                name: 'seekEnd',
                params: {
                    time
                }
            });
            this.isDragging = false;
        },

        /**
         * While dragging the playhead, just change it's position without re-rendering
         * @param event
         * @param state
         */
        onSeek(event, state) {
            this.lastPlayheadPosition = `${state.endX / state.maxX * 100}%`;
            this.refs.playhead.style.left = this.lastPlayheadPosition;
        },

        /**
         * Initiate playhead drag
         * @param event
         */
        seekStartDrag(event) {
            if (this.isDragging) {
                return;
            }
            const drag = SimpleDrag({//eslint-disable-line new-cap
                onDragStart: this.onSeekStart,
                onDragEnd: this.onSeekEnd,
                onDrag: this.onSeek,
                minX: 0,
                maxX: event.target.offsetWidth,
                offsetX: event.nativeEvent.offsetX
            });

            drag.start(event);
        },

        /**
         * Get the storyboard component if it should show
         * @returns {ReactCompositeComponent}
         */
        getStoryboardComp() {
            const showStoryboard = this.props.showStoryboard;
            if (!showStoryboard || showStoryboard === 'none') {
                return null;
            }

            const compName = 'wysiwyg.viewer.components.MediaControlStoryboard';
            const partName = 'storyboard';

            const isVideoPreviewSkin = showStoryboard === 'videoAndTime' && this.props.storyboardUrl && !this.state.storyboardPlaybackError;
            const skin = isVideoPreviewSkin ? partName : `${partName}Minimal`;

            const compProps = this.getStoryboardProps(partName, isVideoPreviewSkin);

            return this.createChildComponent(
                null,
                compName,
                skin,
                compProps
            );
        },

        /**
         * Pass the storyboard a function to register for state changes (so we won't access it directly through its 'ref')
         * @param callback
         */
        setStoryboardStateCallback(callback) {
            this.setStoryboardState = callback;
        },

        /**
         * Get the props to pass to the storyboard
         * @param partName
         * @param isVideoPreviewSkin
         * @returns {{}}
         */
        getStoryboardProps(partName, isVideoPreviewSkin) {
            const props = {
                ref: partName,
                id: this.props.id + partName,
                svgPropsMapForMediaControls: this.props.svgPropsMapForMediaControls,
                hidden: !this.state.isStoryboardVisible,
                setStateCallback: this.setStoryboardStateCallback
            };

            if (isVideoPreviewSkin) {
                props.videoAspectRatio = this.props.videoAspectRatio;
                props.storyboardUrl = this.props.serviceTopologyStaticVideoUrl + this.props.storyboardUrl;
                props.showVideo = true;
                props.playbackErrorCallback = this.handleStoryboardError;
            }
            return props;
        },

        handleStoryboardError() {
            this.setState({storyboardPlaybackError: true});
        },

        /**
         * When starting the hover to show storyboard, re-render to show it
         */
        onStoryboardStart() {
            this.setState({isStoryboardVisible: true});
        },

        /**
         * When hover ends, re-render to hide the storyboard
         */
        onStoryboardEnd() {
            this.setState({isStoryboardVisible: false});
        },

        /**
         * While hovering, call the update function registered by the storyboard and update it's location and play time
         * Also, show ghost playhead
         * @param event
         * @param state
         */
        onStoryboardScrub(event, state) {
            if (this.setStoryboardState && this.props.showStoryboard && this.props.showStoryboard !== 'none') {
                this.setStoryboardState({
                    left: state.endX,
                    time: state.endX / state.maxX * this.state.duration
                });
            }
            this.refs.playheadGhost.style.left = `${state.endX}px`;
        },

        /**
         * Initiate hover to show storyboard
         * @param event
         */
        storyboardStartScrub(event) {
            const drag = SimpleDrag({//eslint-disable-line new-cap
                onDragStart: this.onStoryboardStart,
                onDragEnd: this.onStoryboardEnd,
                onDrag: this.onStoryboardScrub,
                minX: 0,
                maxX: event.target.offsetWidth,
                offsetX: event.clientX - event.currentTarget.getBoundingClientRect().left
            });

            drag.start(event);
        },

        /**
         * Get playhead position -
         * Get from video time if in play state
         * Get from last known position if in seek or drag state
         * @param lastPlayheadPosition last known playhead position
         * @returns {string} number + %
         */
        getPlayHeadPosition(lastPlayheadPosition) {
            const shouldUpdate = !(this.isDragging || isSeeking(this.state.playbackState));
            if (shouldUpdate) {
                return `${this.state.currentTime / this.state.duration * 100}%`;
            }
            return lastPlayheadPosition;
        },

        /**
         * Get the size of the play progress bar
         * While dragging - get from current time in state state
         * while playing - get from the last known playhead position
         * @param playheadPosition last known playhead position
         * @returns {string} number + %
         */
        getProgressPlaySize(playheadPosition) {
            const shouldSyncWithPlayHead = !this.isDragging;

            return shouldSyncWithPlayHead ? playheadPosition : `${this.state.currentTime / this.state.duration * 100}%`;
        },

        /**
         * Get a vectorImage by current symbol name
         * @param {string} iconSymbolName
         * @returns {ReactCompositeComponent}
         */
        getIconComponent(iconSymbolName) {
            const iconProps = _.get(this.props.svgPropsMapForMediaControls, iconSymbolName);
            const skinPartName = `${iconSymbolName}Icon`;
            const props = _.assign({
                id: this.props.id + skinPartName,
                key: iconSymbolName,
                ref: skinPartName,
                preserveViewBox: true
            }, iconProps);
            return this.createChildComponent(null, 'wysiwyg.viewer.components.VectorImage', skinPartName, props);
        },
        /**
         * Get the load progress indicator size
         * @param state
         * @param props
         * @returns {*}
         */
        getProgressLoad(state, props) {
            if (!props.showProgressBuffer) {
                return null;
            }
            return {
                progressLoad: {
                    style: {
                        width: `${state.progress / state.duration * 100}%`
                    }
                }
            };
        },

        getSkinProperties() {
            this.lastPlayheadPosition = this.getPlayHeadPosition(this.lastPlayheadPosition);

            const skinProps = {
                '': {
                    style: this.props.style
                },
                progressDragArea: {
                    onMouseDown: this.seekStartDrag,
                    onMouseEnter: this.storyboardStartScrub

                },
                progressPlay: {
                    style: {
                        width: this.getProgressPlaySize(this.lastPlayheadPosition)
                    }
                },
                playhead: {
                    style: {
                        left: this.lastPlayheadPosition
                    }
                },
                playheadGhost: {
                    hidden: !this.state.isStoryboardVisible
                },
                playheadIcon: this.getIconComponent('playhead'),
                playheadGhostIcon: this.getIconComponent('playheadGhost'),
                storyboard: this.getStoryboardComp()
            };

            _.assign(skinProps, this.getProgressLoad(this.state, this.props));

            return skinProps;
        }
    };
});
